/**
 * **The words for a terrain tile** — its property chips, its one-line property sentences, and the
 * palette groups the TERRAIN window lists the catalogue in (`projects/terrain-tile-types.md` Phases
 * 4–5, `decisions/0185`).
 *
 * Three surfaces name tiles, and they all read this module so one tile never reads two ways: the
 * TERRAIN window's tile palette (chips), the TILE hover pane (`terrainCellDescription.ts`, one
 * sentence per property), and the Map key's "tiles on this map" list (chips).
 *
 * **Mechanics come from the properties, never from the id.** Every function here takes a
 * {@link TerrainProperties} bag resolved through the catalogue (`tileProperties`), so a catalogue
 * edit changes the chips and sentences with it. Only {@link cellTileName} reads `.tile`, and only to
 * name the cell.
 *
 * **The sentences are rules claims** (`tilePropertyList`, which lives in `@lancer-vtt/domain`'s
 * `terrainRegions.ts` so the CLI prints the same words) and are swept by the rules-copy registry
 * (`frontend/src/rulesCopyRegistry.ts`, source `terrain-tiles`). Each is checked against the book:
 *
 *  - Difficult terrain, Core p.62: *"each space of difficult terrain they move into is equivalent to
 *    two spaces of movement"*. The engine charges the cell's `moveCost`, which every catalogue tile
 *    sets to 2.
 *  - Cover, Core p.66: soft cover is *"+1 Difficulty"*, hard cover *"+2 Difficulty"*, and a character
 *    only benefits from hard cover *"if they are adjacent to whatever they're using for cover"*; one
 *    that is obscured by hard cover but not adjacent *"might still get soft cover"*. The engine also
 *    gives a target standing ON a hard-cover cell soft cover (the occupancy floor the TILE pane's
 *    resolved register names), so the hard-cover sentence says where the +2 applies instead of
 *    claiming it for "a target here".
 *  - Dangerous terrain, Core p.62: an ENGINEERING check on first moving into it in a round or ending a
 *    turn in it, 5 damage of the hazard's type on a failure. Core p.13 makes 10 a pass, so the copy
 *    says "target number 10", never "beats 10".
 *  - Flight, Core p.63: a flyer ignores obstructions it could physically pass; `blocksFlight` is the
 *    full-height exception.
 *
 * @module
 */
import {
  CUSTOM_TILE,
  TERRAIN_TILES,
  TILE_TYPE_IDS,
  OPEN_GROUND_TILE,
  tileType,
  type TerrainCell,
  type TerrainProperties,
  type TileTypeId,
  tilePropertyList,
} from "@lancer-vtt/domain";

// The chips and sentences live in the domain package, so the CLI prints the same words.
export { tilePropertyList, type TileProperty, type TilePropertyId } from "@lancer-vtt/domain";

/** What a cell reads as when it carries its own properties rather than a catalogue tile. */
export const CUSTOM_TERRAIN_NAME = "Custom terrain";

/** The chips alone — what the palette and the legend print beside a tile's name. */
export function tileChips(properties: TerrainProperties): string[] {
  return tilePropertyList(properties).map((p) => p.chip);
}

/**
 * The name a stored cell reads as: its catalogue tile's name, {@link CUSTOM_TERRAIN_NAME} for a
 * custom cell, or `null` for open ground (an absent cell). The one place the UI reads `.tile`.
 */
export function cellTileName(entry: TerrainCell | undefined): string | null {
  if (entry === undefined) return null;
  if (entry.tile === CUSTOM_TILE) return CUSTOM_TERRAIN_NAME;
  return tileType(entry.tile)?.name ?? null;
}

// ── The palette ──────────────────────────────────────────────────────────────────────────────────

/** What a tile mainly does — the palette's grouping. */
export type TileGroupId = "cover" | "difficult" | "impassable" | "dangerous";

/**
 * The palette's groups, in the order a GM building a map reaches for them: cover to shelter behind,
 * ground that slows, obstacles, then hazards. Open ground is not a group — it is the palette's
 * separate "clear" button.
 */
export const TILE_GROUPS: { id: TileGroupId; label: string }[] = [
  { id: "cover", label: "Cover" },
  { id: "difficult", label: "Difficult ground" },
  { id: "impassable", label: "Impassable" },
  { id: "dangerous", label: "Dangerous" },
];

/**
 * The group a tile's properties put it in. Harm wins over everything (lava is difficult too, but
 * what a GM needs to know is that it burns), then impassability, then slow ground, then cover.
 * `null` for a tile with no properties (open ground).
 */
export function tileGroup(properties: TerrainProperties): TileGroupId | null {
  if (properties.dangerous === true) return "dangerous";
  if (properties.blocksMovement === true) return "impassable";
  if ((properties.moveCost ?? 1) >= 2) return "difficult";
  if (properties.cover === "soft" || properties.cover === "hard") return "cover";
  return null;
}

/** One palette button: a catalogue tile, its name, and its chips. */
export type PaletteTile = { id: TileTypeId; name: string; blurb: string; chips: string[] };

/**
 * The catalogue as the palette lists it: every tile except open ground, under its group, in
 * catalogue order within a group. The catalogue keeps tiles with the same mechanics next to each
 * other (`scrub` / `scattered-rocks` / `dead-growth`, `wreckage` / `deadfall`), so the pairs sit
 * side by side and their identical chips show they play the same.
 */
export function tilePaletteGroups(): { id: TileGroupId; label: string; tiles: PaletteTile[] }[] {
  return TILE_GROUPS.map(({ id, label }) => ({
    id,
    label,
    tiles: TILE_TYPE_IDS.filter((t) => t !== OPEN_GROUND_TILE && tileGroup(TERRAIN_TILES[t].properties) === id).map((t) => ({
      id: t,
      name: TERRAIN_TILES[t].name,
      blurb: TERRAIN_TILES[t].blurb,
      chips: tileChips(TERRAIN_TILES[t].properties),
    })),
  }));
}
